import type { Task } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import { formatDateProp, rruleWithUntil } from "../ics/escape.js";
import { instantToFloating } from "../zone.js";
import { hexToColorId } from "./color.js";
import {
  buildManagedBlock,
  buildTaskExtendedProps,
  CURRENT_VER,
  eventBodyRev,
  formatTaskTitle,
  TC_EXT_REV,
  TC_EXT_VER,
  type TaskState,
} from "./task-encoding.js";
import type { GoogleEventWrite } from "./types.js";

/**
 * Build the Google Calendar write body for a local task/event. Inverse of
 * {@link mapGoogleEvents}; mirrors `exportIcs` field semantics (all-day → `date`,
 * timed → `dateTime`+`timeZone`, recurrence → RRULE + EXDATE, reminders →
 * overrides) but emits Google JSON instead of an .ics string.
 *
 * Like the ICS exporter, only the base RRULE + cancelled single occurrences
 * (EXDATE) are emitted; thisandfuture shape overrides are not (documented gap).
 *
 * Pass `parent` for dated (detached) subtasks — its title + time appear as a
 * "Part of: …" line in the managed block so Google Calendar shows the context.
 */
export function taskToGoogleEvent(
  task: Task,
  rule?: RecurrenceRule,
  overrides: RecurrenceOverride[] = [],
  subtasks: Task[] = [],
  parent?: Pick<Task, "title" | "due_at" | "all_day">,
  options: { floatingTimeZone?: string | null } = {},
): GoogleEventWrite {
  const tz = task.tz ?? rule?.tz ?? null;
  const allDayTz = task.all_day
    ? allDayDateZone(task.due_at ?? rule?.dtstart ?? null, tz, options.floatingTimeZone ?? null)
    : tz;
  const body: GoogleEventWrite = {
    summary: formatTaskTitle(task) || "(untitled)",
    status: task.status === "cancelled" ? "cancelled" : "confirmed",
  };
  // A TASK (not an event) carries its task-only state in the title marker (set
  // above), a managed notes block, and a private identity bag — see task-encoding.
  if (!task.is_event) {
    // The caller provides subtasks in DFS pre-order (sorted by subtask_order at each
    // level). Do NOT re-sort globally — that would flatten the tree into a wrong order.
    // They render FLAT as emoji (no depth/indentation — see task-encoding).
    const kids = subtasks.filter((s) => !s.deleted_at);
    const parentInfo = parent
      ? parent.due_at
        ? `${parent.title} · ${formatParentDue(parent.due_at, !!parent.all_day)}`
        : parent.title
      : undefined;
    const state: TaskState = {
      priority: task.priority,
      deadline: task.deadline_at ? dateOnly(task.deadline_at) : null,
      subtasks: kids.map((s) => ({ id: s.id, title: s.title, done: s.status === "done" })),
      ...(parentInfo !== undefined ? { parentInfo } : {}),
    };
    const desc = buildManagedBlock(task.notes, state);
    if (desc) body.description = desc;
    body.extendedProperties = { private: buildTaskExtendedProps(state) };
  } else if (task.notes) {
    body.description = task.notes;
  }
  if (task.location) body.location = task.location;
  const colorId = hexToColorId(task.custom_color);
  if (colorId) body.colorId = colorId;

  if (task.due_at) {
    if (task.all_day) {
      const days = task.duration_minutes > 0 ? Math.ceil(task.duration_minutes / 1440) : 1;
      // All-day `due_at` is stored at the event's tz-local midnight (the form and
      // the all-day drag both anchor it that way, and the calendar buckets it by
      // local date). Emit the WALL-CLOCK date in that tz, not the UTC date — for a
      // tz ahead of UTC (e.g. Europe/Paris) the UTC slice lands a day early.
      const startDate = allDayDate(task.due_at, allDayTz);
      body.start = { date: startDate };
      body.end = { date: addDateDays(startDate, days) };
    } else {
      const minutes = task.duration_minutes > 0 ? task.duration_minutes : 0;
      body.start = timed(task.due_at, tz);
      // Google requires end > start for timed events; a zero-duration point
      // reminder gets a 0-length end at the same instant, which Google accepts.
      body.end = timed(addMinutesIso(task.due_at, minutes), tz);
    }
  }

  if (rule) {
    const lines = [`RRULE:${rruleWithUntil(rule.rrule, rule.until)}`];
    const cancels = overrides.filter((o) => o.range === "single" && o.status === "cancelled");
    if (cancels.length) {
      const recurrenceTz = task.all_day ? allDayTz : tz;
      const { params } = formatDateProp(cancels[0]!.recurrence_id_ref, recurrenceTz, task.all_day);
      const values = cancels.map((o) => formatDateProp(o.recurrence_id_ref, recurrenceTz, task.all_day).value);
      lines.push(`EXDATE${params}:${values.join(",")}`);
    }
    body.recurrence = lines;
  }

  body.reminders = {
    useDefault: false,
    overrides: task.reminder_offsets.map((m) => ({ method: "popup", minutes: Math.max(0, m) })),
  };

  return stampRev(body);
}

/**
 * Stamp the write nonce LAST, over the finished body — so it covers time,
 * recurrence, title, notes and machine state together. `taskToGoogleEvent` is
 * pure and deterministic, so recomputing it for a local task later yields the
 * same nonce iff the remote event still holds exactly what we wrote.
 *
 * Plain events get `tcVer`+`tcRev` too (never `tcTask`/`tcState`, so they are
 * still not mistaken for tasks on the way back in — task-ness is `tcTask` or a
 * title marker).
 */
function stampRev(body: GoogleEventWrite): GoogleEventWrite {
  const priv = { ...(body.extendedProperties?.private ?? {}), [TC_EXT_VER]: CURRENT_VER };
  const withVer: GoogleEventWrite = { ...body, extendedProperties: { private: priv } };
  return {
    ...withVer,
    extendedProperties: { private: { ...priv, [TC_EXT_REV]: eventBodyRev(withVer) } },
  };
}

function timed(iso: string, tz: string | null): { dateTime: string; timeZone?: string } {
  return tz ? { dateTime: iso, timeZone: tz } : { dateTime: iso };
}

function dateOnly(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

// Legacy/floating all-day tasks use device-local midnight, but Google-imported
// all-day dates are stored at UTC midnight. Preserve UTC-midnight rows so they
// do not shift backward in zones west of UTC.
function allDayDateZone(iso: string | null, explicitTz: string | null, floatingTimeZone: string | null): string | null {
  if (explicitTz) return explicitTz;
  if (!iso || isUtcMidnight(iso)) return null;
  return floatingTimeZone;
}

function isUtcMidnight(iso: string): boolean {
  const d = new Date(iso);
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/** Wall-clock calendar date (YYYY-MM-DD) of an instant in `tz` — the date the
 *  user sees the all-day event on. `tz` null ⇒ the UTC slice (matches the
 *  UTC-midnight convention pulled-in all-day events are stored with). */
function allDayDate(iso: string, tz: string | null): string {
  return tz ? instantToFloating(iso, tz).toISOString().slice(0, 10) : dateOnly(iso);
}

function addDateDays(date: string, days: number): string {
  const [y, mo, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, mo! - 1, d! + days)).toISOString().slice(0, 10);
}

function addMinutesIso(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"] as const;
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"] as const;

/** "Mon Jun 22" or "Mon Jun 22 at 9:00 AM" from a UTC ISO string. */
function formatParentDue(iso: string, allDay: boolean): string {
  const d = new Date(iso);
  const base = `${DAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  if (allDay) return base;
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mStr = m === 0 ? "" : `:${String(m).padStart(2, "0")}`;
  return `${base} at ${h12}${mStr} ${ampm}`;
}
